/**
 * PEOPLE TAB
 *
 * Per-user execution stats. Answers "who's testing what, and how well?"
 * Driven by `TestCaseExecutionEntity.createdBy` on the backend, which captures
 * whoever last recorded each execution's status.
 *
 * User identities: the backend only stores a Jira accountId per user (no
 * display name / avatar). This tab prefetches full user details for every
 * visible accountId via the shared userCache utility (same pattern used by
 * ScenarioPanel and the edit modals) and re-renders once the cache fills.
 * While the Jira lookup is in flight we show a neutral placeholder.
 */

import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Spinner from '@atlaskit/spinner'
import Avatar from '@atlaskit/avatar'
import { token } from '@atlaskit/tokens'

import MetricCardGrid from '../cards/MetricCardGrid'
import {
  fetchPeopleAsync,
  selectPeople,
  selectReportFilters,
  selectReportLoading,
  selectReportErrors
} from '../../../store/slices/reportsSlice'
import { prefetchUsers, getCachedUserDetails } from '../../../utils/userCache'

const formatDate = (value) => {
  if (!value) return '—'
  const d = new Date(value)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const passRateColor = (rate) => {
  if (rate >= 80) return token('color.text.success', '#22A06B')
  if (rate >= 50) return token('color.text.warning', '#FF991F')
  return token('color.text.danger', '#DE350B')
}

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 500,
  color: token('color.text.subtle', '#6B778C'),
  borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`,
  fontSize: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.3px'
}

const tdStyle = {
  padding: '10px 12px',
  color: token('color.text', '#172B4D'),
  fontSize: '13px',
  borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`
}

const rowBg = (index) =>
  index % 2 === 0
    ? token('elevation.surface', '#FFFFFF')
    : token('color.background.neutral.subtle', '#FAFBFC')

const PeopleTab = ({ level, id }) => {
  const dispatch = useDispatch()
  const people = useSelector(selectPeople)
  const filters = useSelector(selectReportFilters)
  const loading = useSelector(selectReportLoading)
  const errors = useSelector(selectReportErrors)

  // Forces a re-render after the Jira user cache is populated so `getCachedUserDetails`
  // returns display names + avatars. Same pattern as ScenarioPanel.
  const [userCacheVersion, setUserCacheVersion] = useState(0)

  const filterKey = JSON.stringify(filters)

  useEffect(() => {
    if (id && level) {
      dispatch(fetchPeopleAsync({ level, id, filters }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, level, id, filterKey])

  // Prefetch Jira details for every tester visible in this response.
  useEffect(() => {
    const ids = (people?.testers || [])
      .map((t) => t.accountId)
      .filter(Boolean)
    const uniqueIds = [...new Set(ids)]
    if (uniqueIds.length > 0) {
      prefetchUsers(uniqueIds).then(() => {
        setUserCacheVersion((v) => v + 1)
      })
    }
  }, [people])

  // Resolve display name + avatar for every tester up-front. Keyed on
  // userCacheVersion so it re-runs after the bulk fetch resolves.
  const resolvedTesters = useMemo(() => {
    const rows = people?.testers || []
    return rows.map((t) => {
      const cached = getCachedUserDetails(t.accountId)
      return {
        ...t,
        displayName: cached?.displayName || 'Unknown User',
        avatarUrl: cached?.avatarUrl || null
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people, userCacheVersion])

  if (loading.people) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <Spinner size="large" />
      </div>
    )
  }

  if (errors.people) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: token('color.text.danger', '#DE350B') }}>
        <p>Failed to load tester activity</p>
        <p style={{ fontSize: '12px', color: token('color.text.subtle', '#6B778C') }}>{errors.people}</p>
      </div>
    )
  }

  if (!people) return null

  const { totalExecutions = 0, distinctUsers = 0 } = people

  const metrics = [
    { value: distinctUsers, label: 'Active Testers' },
    { value: totalExecutions, label: 'Total Executions' },
    {
      value: distinctUsers > 0 ? (totalExecutions / distinctUsers).toFixed(1) : '0',
      label: 'Avg Executions / Tester'
    }
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <section>
        <MetricCardGrid metrics={metrics} compact gap="12px" />
      </section>

      <section>
        <div
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: token('color.text', '#172B4D'),
            marginBottom: '8px'
          }}
        >
          Per-tester breakdown
        </div>
        {resolvedTesters.length === 0 ? (
          <div
            style={{
              padding: '32px',
              textAlign: 'center',
              color: token('color.text.subtle', '#6B778C'),
              fontSize: '13px',
              border: `1px dashed ${token('color.border', '#DFE1E6')}`,
              borderRadius: '4px'
            }}
          >
            No executions recorded in scope yet.
          </div>
        ) : (
          <div
            style={{
              border: `1px solid ${token('color.border', '#DFE1E6')}`,
              borderRadius: '4px',
              overflow: 'hidden'
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: token('color.background.neutral', '#F4F5F7') }}>
                  <th style={thStyle}>Tester</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Executions</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Pass</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Fail</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Pass rate</th>
                  <th style={thStyle}>Last execution</th>
                </tr>
              </thead>
              <tbody>
                {resolvedTesters.map((t, idx) => (
                  <tr key={t.userId ?? t.accountId ?? idx} style={{ backgroundColor: rowBg(idx) }}>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar size="small" src={t.avatarUrl} name={t.displayName} />
                        <span
                          style={{
                            fontSize: '13px',
                            color: token('color.text', '#172B4D')
                          }}
                        >
                          {t.displayName}
                        </span>
                      </div>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                      {t.executions}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: token('color.text.success', '#22A06B') }}>
                      {t.passCount}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: token('color.text.danger', '#DE350B') }}>
                      {t.failCount}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: 'right',
                        fontWeight: 600,
                        color: passRateColor(t.passRate || 0)
                      }}
                    >
                      {Number(t.passRate || 0).toFixed(1)}%
                    </td>
                    <td style={{ ...tdStyle, color: token('color.text.subtle', '#6B778C') }}>
                      {formatDate(t.lastExecutionDate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

export default PeopleTab
